import { useCallback, useEffect, useState } from 'react';
import { Copy, Link2, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { shortLinkService, type ShortLinkRow } from '../services/shortLinkService';
import { fieldClass, formatDateTime, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

type FormState = {
  code: string;
  targetUrl: string;
};

const emptyForm = (): FormState => ({ code: '', targetUrl: '' });

export function ShortLinksPage() {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ShortLinkRow[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const limit = 15;

  const storefrontBase =
    (import.meta.env.VITE_STOREFRONT_URL as string | undefined)?.replace(/\/$/, '') ||
    'https://evagol.ir';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await shortLinkService.list({ page, limit });
      setRows(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages || 1));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت لینک‌های کوتاه');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(emptyForm());
  };

  const shortUrl = (row: ShortLinkRow) => `${storefrontBase}${row.shortPath || `/l/${row.code}`}`;

  const copyShort = async (row: ShortLinkRow) => {
    try {
      await navigator.clipboard.writeText(shortUrl(row));
      toast.success('لینک کوتاه کپی شد');
    } catch {
      toast.error('کپی ناموفق بود');
    }
  };

  const handleSave = async () => {
    if (!form.targetUrl.trim()) {
      toast.error('آدرس مقصد الزامی است');
      return;
    }
    setSaving(true);
    try {
      await shortLinkService.create({
        code: form.code.trim() || undefined,
        targetUrl: form.targetUrl.trim(),
      });
      toast.success('لینک کوتاه ایجاد شد');
      setFormOpen(false);
      setForm(emptyForm());
      setPage(1);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره لینک کوتاه ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: ShortLinkRow) => {
    if (!confirm(`لینک «${row.code}» حذف شود؟`)) return;
    try {
      await shortLinkService.remove(row.id);
      toast.success('لینک کوتاه حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف لینک کوتاه ناموفق بود');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-2xl">لینک‌های کوتاه</h1>
          <p className="mt-1 text-sm text-slate-500">
            ساخت لینک کوتاه برای محصولات و دسته‌ها؛ مسیر عمومی: <span dir="ltr">/l/&#123;code&#125;</span>
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" />
          لینک جدید
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="admin-table-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-semibold">#</th>
                <th className="px-4 py-3 text-right font-semibold">کد</th>
                <th className="px-4 py-3 text-right font-semibold">لینک کوتاه</th>
                <th className="px-4 py-3 text-right font-semibold">مقصد</th>
                <th className="px-4 py-3 text-right font-semibold">کلیک</th>
                <th className="px-4 py-3 text-right font-semibold">تاریخ</th>
                <th className="px-4 py-3 text-right font-semibold">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    هنوز لینک کوتاهی ثبت نشده است
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-500">{rowNumber(idx, page, limit)}</td>
                    <td className="px-4 py-3 font-mono text-xs" dir="ltr">
                      {row.code}
                    </td>
                    <td className="px-4 py-3" dir="ltr">
                      <div className="flex items-center gap-2">
                        <a
                          href={shortUrl(row)}
                          target="_blank"
                          rel="noreferrer"
                          className="max-w-[220px] truncate text-indigo-600 hover:underline"
                        >
                          {shortUrl(row)}
                        </a>
                        <button
                          type="button"
                          onClick={() => void copyShort(row)}
                          className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-white"
                          title="کپی"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="max-w-[240px] truncate px-4 py-3 text-slate-700" dir="ltr" title={row.targetUrl}>
                      {row.targetUrl}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{row.clickCount.toLocaleString('fa-IR')}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDateTime(row.createdAt)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void handleDelete(row)}
                        className="rounded-lg border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 ? (
          <div className="border-t border-slate-100 px-4 py-3">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title="لینک کوتاه جدید"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="rounded-lg border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ایجاد'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div>
            <label className={labelClass}>آدرس مقصد</label>
            <input
              className={fieldClass}
              dir="ltr"
              value={form.targetUrl}
              onChange={(e) => setForm((f) => ({ ...f, targetUrl: e.target.value }))}
              placeholder="/products/slug یا https://evagol.ir/categories/..."
            />
            <p className="mt-1 text-xs text-slate-500">فقط مسیرهای همین فروشگاه مجاز است.</p>
          </div>
          <div>
            <label className={labelClass}>کد کوتاه (اختیاری)</label>
            <div className="flex items-center gap-2" dir="ltr">
              <span className="text-xs text-slate-500">/l/</span>
              <input
                className={fieldClass}
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="خالی = تولید خودکار"
              />
            </div>
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
              <Link2 className="h-3.5 w-3.5" />
              ۲ تا ۳۲ کاراکتر لاتین، عدد، - یا _
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
